/*
 * PageWright — shared single-page add-to-cart (S4).
 *
 * Real Shopify checkout: POSTs /cart/add.js with the "Collage Art Page" product's tier
 * variant (custom-page-order — real live variants, confirmed against Shopify Admin
 * 2026-07-02) and the EXACT line-item properties the fulfillment webhook expects
 * (~/developer/collagesoup/pagewright packages/api/src/fulfillment.ts `pageLineProps`):
 *   _book_handle → the page's parent book handle
 *   Page         → the page number (string)
 * Getting these property names wrong silently breaks post-purchase delivery — the webhook
 * would find no page-line signature on the order and mint nothing.
 *
 * A third property, _thumb_url (Paul, 2026-07-02: "stuff the thumbnail image of the
 * individual page purchased into the cart... currently just see the default image for
 * custom pages"), carries the REAL page's cover image. "Collage Art Page" is one static
 * product shared by every page purchase, so Shopify's own cart line image would always show
 * the same generic picture — snippets/cart-products.liquid reads this hidden property
 * (underscore-prefixed properties don't show to the customer) and renders it instead of the
 * product's default image when present.
 *
 * VOLUME-TIER FIX (Paul, 2026-07-08 — "after adding 10 images the price in the cart still
 * says 0.99 for number 11+"): every button's data-variant is a STATIC value assigned once at
 * render time (always the tier-1 variant), and this module used to POST it as-is — nothing
 * ever re-priced the cart as it crossed a tier boundary. This is the SAME volume-tier rule
 * packages/domain/src/cart.ts's reducePageCart already solves correctly for the pagewright
 * backend's own (currently unmounted-in-this-theme) cart API — mirrored here by hand since
 * this theme talks straight to Shopify's native /cart/add.js and has no bundler to share that
 * TypeScript module.
 *
 * LIVE LADDER (tasks.db #1828 / #1347 phase T4, 2026-10-08), BEHIND THE THEME SETTING pw_live_tier_ladder
 * (layout/theme.liquid exposes window.PW_LIVE_TIER_LADDER; default OFF). OFF = the legacy hardcoded table
 * below, behaviour identical to before T4 (dev-rules §27: old path intact). ON = the thresholds and the tier
 * variant ids come from the Shopify variants Paul controls (variant metafield
 * csoup_pricing.tier_min_quantity, product metafield tier_count), rendered into the page by
 * snippets/pw-tier-ladder.liquid as <script type="application/json" id="pw-tier-ladder"> (layout/
 * theme.liquid renders it on every page). parseLadder() validates it with the same rules as the API's
 * parsePageTierLadder and FAILS CLOSED: a missing/corrupt/inconsistent ladder means no cart write at
 * all (a cart priced from a guess is worse than a refused add). This file never writes _pw_ladder_v.
 *
 * Shared by pw-book-pages.js (per-book grid) and pw-image-search.js (cross-catalog search) —
 * same button contract, same checkout path, one implementation. Plain classic script (not a
 * module) so consumers can call it via window.PWPageCart without an import-map/bundler — this
 * theme's other vanilla-JS assets follow the same non-module IIFE pattern. Load this script's
 * <script> tag BEFORE the consumer's, both with `defer`, so it's defined in time.
 */
(function () {
  'use strict';

  // tasks.db #1681: this asset may be loaded by several tags on one page (search-header,
  // pw-see-all-pages, ... and, with the tier guard on, layout/theme.liquid). Run exactly once, or the
  // fetch wrapper below would stack and every cart mutation would retier twice.
  if (window.__pwPageCartLoaded) return;
  window.__pwPageCartLoaded = true;

  // @legacy-tiers-begin -- the flag-OFF path (theme setting pw_live_tier_ladder = off, the default). Unchanged from
  // before T4; cleanup phase T6 deletes this block once the live ladder has been ON for >= 14 days.
  // Ascending by minQuantity — mirrors pagewright's packages/contracts/src/pricing.ts.
  var TIER_VARIANTS = [
    { minQuantity: 1, variantId: '52680050835756' }, // 1–10, $0.99/page
    { minQuantity: 11, variantId: '52680050966828' }, // 11–25, $0.69/page
    { minQuantity: 26, variantId: '52680675655980' }, // 26+, $0.59/page
  ];
  var TIER_VARIANT_IDS = TIER_VARIANTS.map(function (t) {
    return t.variantId;
  });

  /** The highest tier whose minQuantity <= quantity (falls back to the lowest tier below it). */
  function resolveTier(quantity) {
    var chosen = TIER_VARIANTS[0];
    for (var i = 0; i < TIER_VARIANTS.length; i++) {
      if (quantity >= TIER_VARIANTS[i].minQuantity) chosen = TIER_VARIANTS[i];
    }
    return chosen;
  }
  // @legacy-tiers-end

  /** Strictly the exact word 'on' turns the live ladder on; anything else (absent, typo, 'ON') = legacy. */
  function liveOn() {
    return window.PW_LIVE_TIER_LADDER === 'on';
  }

  /*
   * The live ladder: [{ minQuantity, variantId }] ascending by minQuantity, or null when the page did
   * not render a usable one. Mirrors packages/domain/src/tier-ladder.ts: a half-edited ladder is
   * REFUSED (never repaired, never defaulted). Read at call time (cheap) so a late-parsed element is
   * still found. Cached nowhere: the rendered element is the only source.
   */
  var LADDER_ID_RE = /^[1-9][0-9]{4,}$/;
  /** Integer >= 1 as a real JSON number (never a string: a text metafield would render quoted). */
  function posInt(n) {
    return typeof n === 'number' && isFinite(n) && n === Math.floor(n) && n >= 1 && n <= Number.MAX_SAFE_INTEGER;
  }
  /** { tiers } on success, { reason } (a short code, no data) on any refusal. */
  function parseLadder() {
    try {
      var el = document.getElementById('pw-tier-ladder');
      if (!el) return { reason: 'absent' };
      var raw = JSON.parse(String(el.textContent || ''));
      if (!raw || typeof raw !== 'object' || !Array.isArray(raw.tiers) || raw.tiers.length === 0) return { reason: 'shape' };
      if (!Array.isArray(raw.unannotated)) return { reason: 'shape' };
      if (raw.unannotated.length > 0) return { reason: 'unannotated' };
      if (!posInt(raw.tierCount)) return { reason: 'count_invalid' };
      if (raw.tierCount !== raw.tiers.length) return { reason: 'count_mismatch' };
      var tiers = [];
      var seenIds = {};
      for (var i = 0; i < raw.tiers.length; i++) {
        var t = raw.tiers[i];
        if (!t || typeof t !== 'object') return { reason: 'shape' };
        var id = String(t.variantId);
        if (!LADDER_ID_RE.test(id)) return { reason: 'bad_id' };
        if (seenIds[id]) return { reason: 'dup' };
        if (!posInt(t.minQuantity)) return { reason: 'bad_min' };
        // The variant price IS the charge: a $0 / missing / non-numeric price is refused (the API refuses it too).
        if (typeof t.price !== 'number' || !isFinite(t.price) || t.price <= 0) return { reason: 'bad_price' };
        seenIds[id] = true;
        tiers.push({ minQuantity: t.minQuantity, variantId: id });
      }
      tiers.sort(function (a, b) {
        return a.minQuantity - b.minQuantity;
      });
      if (tiers[0].minQuantity !== 1) return { reason: 'first_not_one' };
      for (var j = 1; j < tiers.length; j++) {
        if (tiers[j].minQuantity === tiers[j - 1].minQuantity) return { reason: 'dup' };
      }
      return { tiers: tiers };
    } catch (e) {
      return { reason: 'parse' };
    }
  }

  var ladderBeaconSent = false;
  /** PII-free `page_tier_ladder_unavailable` (handle = reason code). Once per page load; never throws. */
  function reportLadderUnavailable(reason) {
    if (ladderBeaconSent) return;
    ladderBeaconSent = true;
    try {
      console.warn('[pw] page-tier ladder unavailable: ' + reason);
      var backendUrl = window.PW_BACKEND_URL;
      if (!backendUrl) return;
      var body = JSON.stringify({
        eventVersion: 1,
        event: 'page_tier_ladder_unavailable',
        handle: String(reason),
        path: /^\/cart/.test(String(window.location && window.location.pathname)) ? '/cart' : '/other',
      });
      var url = backendUrl + '/api/collect';
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else if (typeof fetch !== 'undefined') {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics must never break the cart */
    }
  }

  /** The ladder in force: the legacy table when the flag is off; the validated live ladder (or null) when on. */
  function activeLadder() {
    return liveOn() ? parseLadder() : { tiers: TIER_VARIANTS };
  }
  function pickTier(ladder, quantity) {
    return ladder === TIER_VARIANTS ? resolveTier(quantity) : resolveTierIn(ladder, quantity);
  }

  function ladderIds(tiers) {
    return tiers.map(function (t) {
      return t.variantId;
    });
  }

  /** The highest tier whose minQuantity <= quantity (the lowest tier below it). `tiers` is a validated ladder. */
  function resolveTierIn(tiers, quantity) {
    var chosen = tiers[0];
    for (var i = 0; i < tiers.length; i++) {
      if (quantity >= tiers[i].minQuantity) chosen = tiers[i];
    }
    return chosen;
  }

  function addPageToCart(btn) {
    if (!btn || btn.disabled) return;
    var fallbackVariantId = btn.getAttribute('data-variant');
    var bookHandle = btn.getAttribute('data-parent-handle') || btn.getAttribute('data-book-handle');
    var bookTitle = btn.getAttribute('data-book-title');
    var pageNumber = btn.getAttribute('data-page-number');
    var coverImage = btn.getAttribute('data-cover-image');
    if (!fallbackVariantId || !bookHandle || !pageNumber) return;

    // A click while the "Pricing unavailable" message is showing must not capture the message as the label.
    var originalText = btn.__pwOrigText != null ? btn.__pwOrigText : btn.textContent;
    // FAIL CLOSED (#1828): no usable live ladder = no cart request of any kind. `data-variant` on the
    // button is only a "this is a page button" marker now; it never sets the price.
    var parsed = activeLadder();
    var ladder = parsed.tiers;
    if (!ladder) {
      reportLadderUnavailable(parsed.reason);
      btn.__pwOrigText = originalText;
      btn.textContent = 'Pricing unavailable';
      setTimeout(function () {
        btn.textContent = originalText;
        btn.__pwOrigText = null;
      }, 3000);
      return;
    }
    btn.__pwOrigText = null;
    var ladderVariantIds = ladderIds(ladder);
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.textContent = 'Adding…';

    var newProperties = { _book_handle: bookHandle, Page: pageNumber };
    // Title is the real book name, shown in the native cart in place of the generic shared
    // "Collage Art Page" product title (Paul, 2026-07-08). Not yet threaded through the
    // cross-catalog image-search entry point — that origin still falls back to the generic
    // title until the search API response carries a book title too (follow-up, not this fix).
    if (bookTitle) newProperties.Title = bookTitle;
    if (coverImage) newProperties._thumb_url = coverImage;

    // Read the live cart FIRST — the correct tier depends on the total page count already
    // there, not just this one add. FAIL CLOSED (#1828 review): an unreadable cart is NOT treated as empty
    // (that would add at tier 1 on top of a pile of tier-2 lines); the final .catch offers "Try again".
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) {
          if (liveOn()) throw new Error('cart read failed');
          return { items: [] }; // flag OFF: legacy behaviour (an unreadable cart is treated as empty)
        }
        return r.json();
      })
      .catch(function (e) {
        if (liveOn()) throw e;
        return { items: [] };
      })
      .then(function (cart) {
        // Real incident, 2026-07-17 (order 87246CS): a customer was charged TWICE for the
        // identical page because nothing checked whether it was already in the cart — only
        // the TOTAL page count was tracked (for tier pricing), not WHICH pages. Block it here,
        // before ever reaching /cart/add.js, rather than relying on fulfillment to silently
        // dedupe post-purchase (which is exactly what left that order PARTIALLY_FULFILLED —
        // she wasn't missing content, but she was billed twice for one file).
        var isDuplicate = (cart.items || []).some(function (item) {
          return item.properties &&
            item.properties._book_handle === bookHandle &&
            item.properties.Page === pageNumber;
        });
        if (isDuplicate) {
          return Promise.resolve({ duplicate: true });
        }

        var existingPageLines = (cart.items || []).filter(function (item) {
          return ladderVariantIds.indexOf(String(item.variant_id)) !== -1;
        });
        var existingCount = existingPageLines.reduce(function (sum, item) {
          return sum + item.quantity;
        }, 0);
        var target = pickTier(ladder, existingCount + 1);
        var allAtTarget = existingPageLines.every(function (item) {
          return String(item.variant_id) === target.variantId;
        });

        if (existingPageLines.length === 0 || allAtTarget) {
          // No tier crossing: just add the new page at the (already-correct) target variant.
          return postAdd([{ id: target.variantId, quantity: 1, properties: newProperties }]);
        }

        // Tier crossing: zero every existing page-tier line, then re-add ALL of them (carrying
        // forward their own book/page/thumb properties) plus the new page, at the new tier —
        // the same "zero + rebuild" op-list reducePageCart emits, applied via the native cart API.
        var zeroUpdates = {};
        existingPageLines.forEach(function (item) {
          zeroUpdates[item.key] = 0;
        });
        var rebuildItems = existingPageLines.map(function (item) {
          var props = { _book_handle: item.properties._book_handle, Page: item.properties.Page };
          if (item.properties.Title) props.Title = item.properties.Title;
          if (item.properties._thumb_url) props._thumb_url = item.properties._thumb_url;
          return { id: target.variantId, quantity: item.quantity, properties: props };
        });
        rebuildItems.push({ id: target.variantId, quantity: 1, properties: newProperties });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: zeroUpdates }),
        }).then(function () {
          return postAdd(rebuildItems);
        });
      })
      .then(function (result) {
        if (result && result.duplicate) {
          btn.textContent = 'Already in cart';
          setTimeout(function () {
            btn.disabled = false;
            btn.removeAttribute('aria-disabled');
            btn.textContent = originalText;
          }, 2000);
          return;
        }
        btn.textContent = 'Added ✓';
        btn.setAttribute('data-pw-added', '');
        refreshCartBubble();
        setTimeout(function () {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('data-pw-added');
          btn.textContent = originalText;
        }, 2000);
      })
      .catch(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.textContent = 'Try again';
      });
  }

  function postAdd(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (resp) {
      if (!resp.ok) {
        return resp.json().then(function (e) {
          throw new Error((e && e.description) || 'Add to cart failed');
        });
      }
      return resp.json();
    });
  }

/*
 * Updates every cart-bubble on the page (snippets/cart-bubble.liquid — desktop + mobile
 * instances) directly via DOM + /cart.js, rather than through Shopify's native cart-update
 * path. Deliberate: that native path is View-Transition-based and broke site-wide earlier
 * this session from an unrelated duplicate-id bug (see cart-bubble.liquid's id_suffix param) —
 * a plain independent fetch + DOM write here can't trigger the same class of failure.
 */
  function refreshCartBubble() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var counts = document.querySelectorAll('.cart-bubble__text-count');
        for (var i = 0; i < counts.length; i++) {
          counts[i].textContent = cart.item_count;
          counts[i].classList.toggle('hidden', cart.item_count === 0);
        }
        var bubbles = document.querySelectorAll('.cart-bubble');
        for (var j = 0; j < bubbles.length; j++) {
          bubbles[j].classList.toggle('visually-hidden', cart.item_count === 0);
        }
      })
      .catch(function () {
        /* cart count refresh is cosmetic — the add itself already succeeded */
      });
  }

  /*
   * PAGE-TIER GUARD (tasks.db #1681, 2026-09-29; Paul-approved). Theme setting `pw_page_tier_guard`
   * (default off) is exposed as window.PW_PAGE_TIER_GUARD by layout/theme.liquid, which also loads
   * this asset site-wide while the guard is on. Guard off = every behavior below is inert and the
   * asset behaves exactly as before. Guard on adds: retier on load, a checkout hold, and a
   * PII-free `page_tier_reconcile` beacon. Everything FAILS OPEN: if the retier fails or takes too
   * long, checkout proceeds (the server-side detector still records the order).
   */
  var committing = false; // a retier is between its zero step and the end of its re-add
  var holding = false; // a checkout hold is in progress
  var resumingCheckout = false; // checkout is being created: no further cart writes
  var cartMutated = false; // a rebuild has been sent since the page rendered (rendered forms are stale)
  function guardOn() {
    return window.PW_PAGE_TIER_GUARD === 'on';
  }

  /** t1..tN (ordinal) for a tier variant id (non-numeric, so it is safe to send anywhere). */
  function tierId(ladder, variantId) {
    var i = ladderIds(ladder).indexOf(String(variantId));
    return i === -1 ? 'unknown' : 't' + (i + 1);
  }

  /** Rule 13 analytics: fields are tier ids, a page count and a coarse surface. No PII. */
  function reportReconcile(fromTiers, toTier, pageCount) {
    if (!guardOn()) return;
    try {
      var backendUrl = window.PW_BACKEND_URL;
      if (!backendUrl) return;
      var body = JSON.stringify({
        eventVersion: 1,
        event: 'page_tier_reconcile',
        handle: fromTiers + '->' + toTier,
        resultCount: pageCount,
        path: /^\/cart/.test(String(window.location && window.location.pathname)) ? '/cart' : '/other',
      });
      var url = backendUrl + '/api/collect';
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else if (typeof fetch !== 'undefined') {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics must never break the cart */
    }
  }

  /*
   * RE-TIER AFTER A REMOVAL (2026-07-30 re-audit, B4).
   *
   * Volume-tier resolution lived ONLY inside addPageToCart, so it ran on the way up and never on
   * the way down. Add 11 pages -> all lines move to tier 2 ($0.69). Remove 5 -> six lines remain
   * at $0.69 even though the cart now qualifies only for tier 1 ($0.99), and Shopify charges the
   * variant price on the line. Checkout takes $4.14 instead of $5.94.
   *
   * It is trivially repeatable on purpose: add 11, remove 10, buy 1 at the bulk rate. Nothing
   * re-prices server-side — there is no Shopify Function or cart transform on this store — so the
   * cart line IS the price. Removal is a real, intended path (snippets/cart-products.liquid,
   * assets/component-cart-items.js posts quantity 0).
   *
   * `packages/domain/src/cart.ts` has always handled this correctly; the theme hand-port
   * implemented only the add half, and the existing tier test only ever asserted the add path.
   *
   * Deliberately a no-op when every page line already sits at the correct tier, so the common
   * case costs one /cart.js read and nothing else. The customer is never overcharged by this —
   * it only ever corrects a line that is priced BELOW what the cart now qualifies for.
   */
  function retierPageLines() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        // #1828: the retier is CORRECTION-only. With no usable live ladder it cannot tell a page line
        // from any other line, so it does nothing (checkout is never trapped; the server detector records).
        var activeL = activeLadder();
        var ladder = activeL.tiers;
        if (!ladder) {
          reportLadderUnavailable(activeL.reason); // inert, but never silent (dev-rules §36); once per page load
          return null;
        }
        var ladderVariantIds = ladderIds(ladder);
        var pageLines = (cart.items || []).filter(function (item) {
          return ladderVariantIds.indexOf(String(item.variant_id)) !== -1;
        });
        if (pageLines.length === 0) return null;

        var count = pageLines.reduce(function (sum, item) { return sum + item.quantity; }, 0);
        var target = pickTier(ladder, count);
        var allAtTarget = pageLines.every(function (item) {
          return String(item.variant_id) === target.variantId;
        });
        if (allAtTarget) return null; // already correct — the overwhelmingly common case

        var zeroUpdates = {};
        pageLines.forEach(function (item) { zeroUpdates[item.key] = 0; });
        var rebuildItems = pageLines.map(function (item) {
          var props = { _book_handle: item.properties._book_handle, Page: item.properties.Page };
          if (item.properties.Title) props.Title = item.properties.Title;
          if (item.properties._thumb_url) props._thumb_url = item.properties._thumb_url;
          return { id: target.variantId, quantity: item.quantity, properties: props };
        });

        var fromIds = [];
        pageLines.forEach(function (item) {
          var id = tierId(ladder, item.variant_id);
          if (fromIds.indexOf(id) === -1) fromIds.push(id);
        });
        var originalItems = pageLines.map(function (item) {
          return { id: item.variant_id, quantity: item.quantity, properties: item.properties || {} };
        });

        // From here the cart is transiently empty of page lines: `committing` tells the checkout hold
        // it must NOT resume until the rebuild has settled (3-model review, 2026-09-29).
        // Checkout is already being created (the hold's fail-open cap fired first): do not start a
        // rebuild that would land mid-navigation.
        if (resumingCheckout) return null;
        committing = true;
        cartMutated = true;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: zeroUpdates }),
        }).then(function (zeroResp) {
          if (zeroResp && zeroResp.ok === false) throw new Error('zero step failed');
          return postAdd(rebuildItems).catch(function (err) {
            // The re-add failed after the zeroing step: put the customer's pages back (best effort)
            // so a failed retier can never leave an empty/half cart behind.
            return postAdd(originalItems).then(function () { throw err; }, function () { throw err; });
          });
        }).then(function (added) {
          committing = false;
          reportReconcile(fromIds.join('+'), tierId(ladder, target.variantId), count);
          // The theme's rendered cart (line keys, prices) is now stale. On the /cart page reload once
          // (the rebuilt cart is correct, so this cannot loop). At checkout the hold navigates instead.
          if (guardOn() && !holding && !resumingCheckout && /^\/cart/.test(String(window.location && window.location.pathname))) {
            try { window.location.reload(); } catch (e) { /* cosmetic */ }
          }
          return added || { changed: true };
        }, function (err) {
          committing = false;
          throw err;
        });
      })
      .catch(function () {
        /* never block the cart UI on a re-tier; the next add also reconciles */
        return null;
      });
  }

  /*
   * Watch for cart mutations we did not initiate. Removal happens through the theme's own cart
   * components, which post /cart/change.js. Guarded by `retiering` so our OWN rebuild (which
   * posts /cart/update.js + /cart/add.js) cannot recurse.
   */
  var retiering = false;
  var inflight = null;
  function guardedRetier() {
    if (retiering) return;
    retiering = true;
    inflight = Promise.resolve(retierPageLines()).then(function () {
      retiering = false;
    }, function () {
      retiering = false;
    });
  }

  if (typeof window.fetch === 'function') {
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var mutating = /\/cart\/(change|update|clear)(\.js)?/.test(String(url));
      return nativeFetch(input, init).then(function (res) {
        if (mutating && !retiering) setTimeout(guardedRetier, 0);
        return res;
      });
    };
  }
  ['cart:updated', 'cart:refresh'].forEach(function (name) {
    document.addEventListener(name, function () { setTimeout(guardedRetier, 60); });
  });

  var ON_LOAD_RETIER_DELAY_MS = 100;
  // Guard on: retier a cart that ARRIVED mis-tiered (another tab, recovery email, permalink).
  if (guardOn()) {
    // Deferred a beat so it never competes with the page's own first paint/work; a checkout press
    // inside this window does its own retier (below), and this then finds a correct cart (no-op).
    var onLoadRetier = function () { setTimeout(guardedRetier, ON_LOAD_RETIER_DELAY_MS); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onLoadRetier);
    else onLoadRetier();
  }

  // Guard on: hold Horizon's checkout button (a submit named "checkout" in the /cart form) until the
  // cart is correctly tiered, then continue to checkout. Fail open on any error or after the cap.
  var holdPromise = null;
  function holdCapMs() {
    return Number(window.PW_PAGE_TIER_HOLD_MS) > 0 ? Number(window.PW_PAGE_TIER_HOLD_MS) : 4000;
  }
  var COMMIT_CAP_MS = 8000;
  function isCartCheckoutSubmit(ev) {
    var form = ev && ev.target;
    if (!form || !form.getAttribute) return false;
    var action = String(form.getAttribute('action') || '');
    if (!/(^|\/)cart\/?$/.test(action.split('?')[0])) return false;
    var submitter = ev.submitter;
    return !!(submitter && submitter.name === 'checkout');
  }
  function setBusy(el, busy) {
    try {
      el.disabled = busy;
      if (busy && el.setAttribute) el.setAttribute('aria-busy', 'true');
      if (!busy && el.removeAttribute) el.removeAttribute('aria-busy');
    } catch (e) { /* cosmetic */ }
  }
  document.addEventListener('submit', function (ev) {
    if (!guardOn() || resumingCheckout || !isCartCheckoutSubmit(ev)) return;
    ev.preventDefault();
    if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
    // A second press during a hold is swallowed: ONE hold, ONE rebuild, ONE checkout (a second
    // concurrent rebuild would merge identical re-adds into quantity-2 lines = double charge).
    if (holdPromise) return;
    var form = ev.target;
    var submitter = ev.submitter;
    holding = true;
    setBusy(submitter, true);
    var changed = false;
    // Mark the retier in flight so the deferred on-load retier and the fetch watcher do not start a
    // second rebuild alongside this one.
    var work = Promise.resolve(inflight)
      .then(function () {
        retiering = true;
        return retierPageLines();
      })
      .then(function (r) { changed = !!r; }, function () { /* fail open */ })
      .then(function () { retiering = false; });
    holdPromise = work;
    inflight = work;
    var timeout = new Promise(function (r) { setTimeout(r, holdCapMs()); });
    Promise.race([work, timeout]).then(function () {
      // The cap only bounds the read/decision phase. Once the zeroing step has been sent, wait for the
      // re-add to settle (own longer cap) so checkout never opens on a half-built cart.
      if (committing) {
        return Promise.race([work, new Promise(function (r) { setTimeout(r, COMMIT_CAP_MS); })]);
      }
    }).then(function () {
      resumingCheckout = true;
      setTimeout(function () { resumingCheckout = false; }, 10000); // re-arm if checkout did not navigate
      holding = false;
      holdPromise = null;
      setBusy(submitter, false);
      // After a corrective retier the rendered form no longer matches the cart (line order changed),
      // so go straight to /checkout instead of re-submitting it.
      if (changed || cartMutated || !form.isConnected || typeof form.requestSubmit !== 'function') {
        window.location.href = '/checkout';
        return;
      }
      try {
        form.requestSubmit(submitter);
      } catch (e) {
        window.location.href = '/checkout';
      }
    });
  }, true);

  window.PWPageCart = {
    addPageToCart: addPageToCart,
    refreshCartBubble: refreshCartBubble,
    retierPageLines: retierPageLines,
  };
})();
